import { useMemo } from 'react';
import { getNavIcon } from '@/lib/navigation-icons';
import { useNavigationApi, resolveNodeRoute, resolveIcon, type NavigationGroup } from './useNavigationApi';


/** Re-export NavigationGroup for consumers */
export type { NavigationGroup } from './useNavigationApi';

// ─── Mock data (matches the existing hardcoded navigation) ───

// ─── RoadHaven IA: customer-journey grouping (Showroom / Fleet / Aftercare / Rewards / Hub) ───

const showroomDropdownItems = [
  { name: "Browse Catalog", path: "/parts", tag: "Parts" },
  { name: "Quick Order Pad", path: "/quick-order", tag: "QuickOrder" },
  { name: "Saved Carts", path: "/saved-carts", tag: "SavedCarts" },
  { name: "My Favorites", path: "/my-favorites", tag: "Favorites" },
  { name: "Identify a Part (AI)", path: "/unknown-part-request", tag: "UnknownPartRequest" },
];

const fleetDropdownItems = [
  { name: "RV Lookup", path: "/units", tag: "UnitSearch" },
  { name: "Stock Transfers", path: "/inventory-transfers", tag: "InventoryTransfers" },
  { name: "Unit Registration", path: "/warranty-registration", tag: "WarrantyRegistration" },
];

const aftercareDropdownItems = [
  { name: "Open Tickets", path: "/your-tickets", tag: "YourTickets" },
  { name: "Claims & Pre-Auths", path: "/dealer-claims", tag: "ClaimsAndPreAuths" },
  { name: "Returns (RGAs)", path: "/rga-list", tag: "Rgas" },
  { name: "Recall & Campaign Center", path: "/service-campaigns", tag: "ServiceCampaigns" },
  { name: "Labor Time Guide", path: "/labor-operations", tag: "LaborOperationsLibrary" },
];

const rewardsDropdownItems = [
  { name: "My Incentives", path: "/my-spiffs", tag: "MyClaimedSpiffs" },
  { name: "Dealership Incentives", path: "/claimed-dealer-spiffs", tag: "DealerClaimedSpiffs" },
  { name: "Salesperson Program", path: "/salesperson-spiffs", tag: "SalesPersonSpiffs" },
  { name: "Active Promotions", path: "/dealer-spiffs", tag: "DealerSpiffs" },
  { name: "Statements", path: "/dealer-invoices", tag: "DealerInvoices" },
];

const hubDropdownItems = [
  { name: "Announcements", path: "/message-board", tag: "MessageBoard" },
  { name: "Brand Library", path: "/digital-assets", tag: "DigitalAssets" },
  { name: "Case Manager", path: "/case-management", tag: "Cases" },
];

interface MockNavItem {
  name: string;
  type: 'dropdown' | 'link';
  path?: string;
  items?: Array<{ name: string; path: string; tag?: string }>;
  isPartsMenu?: boolean;
}

const baseMockNavigationItems: MockNavItem[] = [
  { name: "Showroom", type: "dropdown", items: showroomDropdownItems, isPartsMenu: true },
  { name: "Fleet", type: "dropdown", items: fleetDropdownItems },
  { name: "Aftercare", type: "dropdown", items: aftercareDropdownItems },
  { name: "Rewards", type: "dropdown", items: rewardsDropdownItems },
  { name: "Hub", type: "dropdown", items: hubDropdownItems },
];

export function useMainNavigation() {
  const { nodes, isLoading, useMock } = useNavigationApi({
    rootNavigationTag: 'MainMenu',
    stopLevel: 3,
  });
  // Build API-driven navigation groups from nodes
  const apiGroups = useMemo<NavigationGroup[] | null>(() => {
    if (!nodes) return null;
    return nodes
      .filter((node: any) => node.showInMenu !== false)
      .map((node: any) => {
        const title = node.name || '';
        const children = (node.nodes || [])
          .filter((child: any) => child.showInMenu !== false)
          .map((child: any) => {
            const childTitle = child.name || '';
            return {
              title: childTitle,
              url: resolveNodeRoute(child),
              icon: resolveIcon(child.navigationTag),
            };
          });

        const hasChildren = children.length > 0;
        const resolvedUrl = resolveNodeRoute(node);

        return {
          title,
          type: hasChildren ? 'dropdown' : 'link',
          url: (node.isClickable === false && (hasChildren || title !== 'Message Board')) ? '#' : resolvedUrl,
          icon: resolveIcon(node.navigationTag),
          children,
        } as NavigationGroup;
      });
  }, [nodes]);

  const mockGroups = useMemo(() => {
    return baseMockNavigationItems;
  }, []);

  return {
    /** API-driven groups (null if useMock or not yet loaded) */
    apiGroups,
    /** Mock navigation items */
    mockItems: mockGroups,
    isLoading,
    useMock,
  };
}
